import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { clearPendingOpenOrderSummary } from "@components/OrderTaking/Store/feature";
import { OrderSliceState } from "@components/OrderTaking/Store/feature/types";

type RootSlice = { orderSlice: OrderSliceState };

/**
 * Drives the "Pay → AdditionalSale → Order Summary" chaining.
 *
 * Watches the AdditionalSale dialog open→closed transition. When the dialog
 * closes while `pendingOpenOrderSummary` is still set (i.e. the dialog was
 * opened from the Pay button), it calls `onNavigate` to open the order summary
 * and then consumes the flag via `clearPendingOpenOrderSummary` so it fires
 * exactly once.
 *
 * Shared by BasketActionButtons and PrmBasket to avoid duplicating this logic.
 *
 * @param onNavigate opens the order summary for the caller. May change identity
 *   between renders (read through a ref) and may be undefined; the hook no-ops
 *   the navigation call in that case but still consumes the pending flag.
 */
export function usePostAdditionalSaleNavigation(
    onNavigate: (() => void) | undefined
): void {
    const dispatch = useDispatch();
    const isAdditionalSaleOpen = useSelector(
        (state: RootSlice) => state.orderSlice.isAdditionalSaleOpen
    );
    const pendingOpenOrderSummary = useSelector(
        (state: RootSlice) => state.orderSlice.pendingOpenOrderSummary
    );

    // Keep the latest onNavigate in a ref so it is NOT an effect dependency.
    // Otherwise a consumer that re-creates the callback (e.g. on a summary
    // toggle) would re-run the effect and could fire navigation twice.
    const onNavigateRef = React.useRef(onNavigate);
    onNavigateRef.current = onNavigate;

    // Init to false on purpose: this hook reacts to a true→false TRANSITION of
    // the dialog, never to the initial mounted state. Seeding from the current
    // Redux value could mis-handle a conditional mount.
    const prevDialogOpenRef = React.useRef(false);
    React.useEffect(() => {
        if (
            prevDialogOpenRef.current &&
            !isAdditionalSaleOpen &&
            pendingOpenOrderSummary
        ) {
            onNavigateRef.current?.();
            dispatch(clearPendingOpenOrderSummary());
        }
        prevDialogOpenRef.current = isAdditionalSaleOpen;
        // onNavigate is intentionally excluded — it is read via onNavigateRef.
    }, [isAdditionalSaleOpen, pendingOpenOrderSummary, dispatch]);
}

export default usePostAdditionalSaleNavigation;
